import { DocType, IntelliCheckReportModules, SubmittedData } from 'api/file/interfaces/get-file-order-insights';

export interface IdentityField {
  label: string;
  frontValue?: string;
  backValue?: string;
  isMismatch?: boolean;
}

export const getIdentityFields = (
  reportData: IntelliCheckReportModules,
  submittedData: SubmittedData,
): { fields: IdentityField[]; isPassportOrOther: boolean; isDL: boolean } => {
  const { idcheck, ocr, ocrMatch, extendedId } = reportData;

  const isDL = Boolean(submittedData.docType === DocType.NORTH_AMERICAN_DRIVER_LICENSE);
  const isPassportOrOther = Boolean(
    submittedData.docType === DocType.PASSPORT || submittedData.docType === DocType.OTHER,
  );

  const backData = isPassportOrOther ? extendedId?.data : idcheck?.data;
  const expirationDateMatchScore = ocrMatch?.data?.expirationDateMatchDetails?.similarityScore;
  const dobMatchScore = ocrMatch?.data?.dobMatchDetails?.similarityScore;
  const issueDateMatchScore = ocrMatch?.data?.issueDateMatchDetails?.similarityScore;
  const fullScore = 100;

  const fields: IdentityField[] = [
    {
      label: 'Issuer',
      frontValue: ocr?.data?.issuerName,
      backValue: isPassportOrOther ? extendedId?.data.issuerName : idcheck?.data?.issuingJurisdictionCvt,
      isMismatch: !ocrMatch?.data?.isIssuerNameMatch,
    },
    {
      label: 'Name',
      frontValue: `${ocr?.data?.firstName || ''} ${ocr?.data?.lastName || ''}`.trim(),
      backValue: `${backData?.firstName || ''} ${idcheck?.data?.middleName || ''} ${backData?.lastName || ''}`.trim(),
      isMismatch: !ocrMatch?.data?.isNameMatch,
    },
    {
      label: 'Address',
      frontValue: ocr?.data?.address,
      backValue: isDL
        ? [
            idcheck?.data?.address1,
            idcheck?.data?.address2,
            idcheck?.data?.city,
            idcheck?.data?.state,
            idcheck?.data?.postalCode,
          ]
            .filter(Boolean)
            .join(', ')
            .trim()
        : extendedId?.data.address || '',
      isMismatch: !ocrMatch?.data?.isAddressMatch,
    },
    {
      label: 'Date of Birth',
      frontValue: ocr?.data?.dateOfBirthFormatted,
      backValue: isDL ? idcheck?.data?.dateOfBirth : extendedId?.data?.dateOfBirthFormatted,
      isMismatch: dobMatchScore === undefined || dobMatchScore < fullScore,
    },
    ...(isPassportOrOther
      ? [
          {
            label: 'Age',
            frontValue: ocr?.data?.age,
            backValue: extendedId?.data.age,
            isMismatch: !ocrMatch?.data?.isDobMatch,
          },
        ]
      : []),
    {
      label: 'Expiration Date',
      frontValue: ocr?.data?.dateOfExpiryFormatted || ocr?.data?.dateOfExpiry,
      backValue: isDL ? idcheck?.data?.expirationDate : extendedId?.data.dateOfExpiryFormatted,
      isMismatch: expirationDateMatchScore === undefined || expirationDateMatchScore < fullScore,
    },
    {
      label: 'Issue Date',
      frontValue: ocr?.data?.dateOfIssueFormatted || ocr?.data?.dateOfIssue,
      backValue: isDL ? idcheck?.data?.issueDate : extendedId?.data.dateOfIssueFormatted,
      isMismatch: issueDateMatchScore === undefined || issueDateMatchScore < fullScore,
    },
    {
      label: 'ID Number',
      frontValue: ocr?.data?.documentNumber,
      backValue: isDL ? idcheck?.data?.dLIDNumberFormatted : extendedId?.data.documentNumber,
      isMismatch: !ocrMatch?.data?.isDocumentNumberMatch,
    },
    ...(isPassportOrOther
      ? [
          {
            label: 'Country Code',
            frontValue: ocr?.data?.countryCode || '-',
            backValue: extendedId?.data.countryCode || '-',
            isMismatch: !ocrMatch?.data?.isCountryCodeMatch,
          },
          {
            label: 'Nationality',
            frontValue: ocr?.data?.nationality,
            backValue: extendedId?.data.nationality || '-',
            isMismatch: !ocrMatch?.data?.isNationalityMatch,
          },
        ]
      : []),
    {
      label: 'Gender',
      frontValue: ocr?.data?.sex,
      backValue: isDL ? idcheck?.data?.gender : extendedId?.data.sex,
      isMismatch: !ocrMatch?.data?.isSexMatch,
    },
    ...(isDL
      ? [
          {
            label: 'Drive Class',
            frontValue: ocr?.data?.dlClass,
            backValue: idcheck?.data?.driverClass,
            isMismatch: !ocrMatch?.data?.isDlClassMatch,
          },
          {
            label: 'Height (feet & inches)',
            frontValue: ocr?.data?.height,
            backValue: idcheck?.data?.heightFeetInches,
            isMismatch: !ocrMatch?.data?.isHeightMatch,
          },
          {
            label: 'Is Duplicate',
            frontValue: '-',
            backValue: idcheck?.data?.isDuplicate?.toString(),
          },
        ]
      : []),
    {
      label: 'Doc Type',
      frontValue: '-',
      backValue: isDL ? idcheck?.data?.docType : extendedId?.data.documentType,
    },
    {
      label: 'Doc Category',
      frontValue: '-',
      backValue: isDL ? idcheck?.data?.docCategory : extendedId?.data.documentName,
    },
    ...(isPassportOrOther
      ? [
          {
            label: 'Place of Birth',
            frontValue: ocr?.data?.placeOfBirth,
            backValue: extendedId?.data.placeOfBirth || '-',
          },
        ]
      : []),
  ];

  return { fields, isPassportOrOther, isDL };
};
